import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'step1',
    'step2',
    'step3',
    'step4',
    'step1Indicator',
    'step2Indicator',
    'step3Indicator',
    'step4Indicator',
    'nameInput',
    'hypothesisInput',
    'descriptionInput',
    'visibilityInput',
    'variablesList',
    'variableTemplate',
    'metricCheckbox',
    'baselineStartInput',
    'baselineEndInput',
    'experimentStartInput',
    'experimentEndInput',
    'indefiniteCheckbox',
    'submitBtn'
  ]

  static values = {
    accountId: String
  }

  currentStep = 1

  connect() {
    // Check for pre-filled data from suggestions
    this.loadFromQueryParams()

    // Add initial variable if none exist
    if (this.variablesListTarget.children.length === 0) {
      this.addVariable()
    }
  }

  loadFromQueryParams() {
    const params = new URLSearchParams(window.location.search)

    if (params.has('name') && this.hasNameInputTarget) {
      this.nameInputTarget.value = params.get('name')
    }

    if (params.has('hypothesis') && this.hasHypothesisInputTarget) {
      this.hypothesisInputTarget.value = params.get('hypothesis')
    }

    if (params.has('variables')) {
      try {
        const variables = JSON.parse(params.get('variables'))
        variables.forEach((v) => this.addVariableWithData(v))
      } catch (e) {
        console.error('Error parsing variables:', e)
      }
    }

    if (params.has('metrics')) {
      try {
        const metrics = JSON.parse(params.get('metrics'))
        metrics.forEach((metricType) => {
          const checkbox = this.metricCheckboxTargets.find((cb) => cb.value === metricType)
          if (checkbox) checkbox.checked = true
        })
      } catch (e) {
        console.error('Error parsing metrics:', e)
      }
    }
  }

  nextStep() {
    if (!this.validateCurrentStep()) return

    this.currentStep += 1
    this.updateStepVisibility()
  }

  prevStep() {
    this.currentStep -= 1
    this.updateStepVisibility()
  }

  updateStepVisibility() {
    // Hide all steps
    [this.step1Target, this.step2Target, this.step3Target, this.step4Target].forEach((step, index) => {
      step.classList.toggle('hidden', index + 1 !== this.currentStep)
    })

    // Update indicators
    this.updateIndicators()
  }

  updateIndicators() {
    const indicators = [
      this.step1IndicatorTarget,
      this.step2IndicatorTarget,
      this.step3IndicatorTarget,
      this.step4IndicatorTarget
    ]

    indicators.forEach((indicator, index) => {
      const stepNum = index + 1
      const circle = indicator.querySelector('span:first-child')

      if (stepNum < this.currentStep) {
        // Completed step
        indicator.classList.remove('text-gray-400')
        indicator.classList.add('text-gray-900', 'font-bold')
        circle.classList.remove('border-gray-300')
        circle.classList.add('border-gray-900', 'bg-gray-900', 'text-white')
      } else if (stepNum === this.currentStep) {
        // Current step
        indicator.classList.remove('text-gray-400')
        indicator.classList.add('text-gray-900', 'font-bold')
        circle.classList.remove('border-gray-300')
        circle.classList.add('border-gray-900', 'bg-gray-900', 'text-white')
      } else {
        // Future step
        indicator.classList.remove('text-gray-900', 'font-bold')
        indicator.classList.add('text-gray-400')
        circle.classList.remove('border-gray-900', 'bg-gray-900', 'text-white')
        circle.classList.add('border-gray-300')
      }
    })
  }

  validateCurrentStep() {
    switch (this.currentStep) {
      case 1:
        if (!this.nameInputTarget.value.trim()) {
          this.showNotification('Please enter an experiment name', 'error')
          this.nameInputTarget.focus()
          return false
        }
        return true

      case 2: {
        const variables = this.variablesListTarget.querySelectorAll('.variable-item')
        if (variables.length === 0) {
          this.showNotification('Please add at least one variable', 'error')
          return false
        }
        // Check each variable has a name
        const hasEmptyName = Array.from(variables).some((variable) => {
          const nameInput = variable.querySelector('input[name*="[name]"]')
          if (!nameInput.value.trim()) {
            this.showNotification('All variables must have a name', 'error')
            nameInput.focus()
            return true
          }
          return false
        })
        return !hasEmptyName
      }

      case 3: {
        const selectedMetrics = this.metricCheckboxTargets.filter((cb) => cb.checked)
        if (selectedMetrics.length === 0) {
          this.showNotification('Please select at least one metric to track', 'error')
          return false
        }
        return true
      }

      default:
        return true
    }
  }

  addVariable() {
    const template = this.variableTemplateTarget.content.cloneNode(true)
    this.variablesListTarget.appendChild(template)
  }

  addVariableWithData(data) {
    const template = this.variableTemplateTarget.content.cloneNode(true)

    const nameInput = template.querySelector('input[name*="[name]"]')
    const typeSelect = template.querySelector('select[name*="[variable_type]"]')
    const dosageInput = template.querySelector('input[name*="[dosage]"]')
    const frequencyInput = template.querySelector('input[name*="[frequency]"]')

    if (nameInput && data.name) nameInput.value = data.name
    if (typeSelect && data.variable_type) typeSelect.value = data.variable_type
    if (dosageInput && data.dosage) dosageInput.value = data.dosage
    if (frequencyInput && data.frequency) frequencyInput.value = data.frequency

    this.variablesListTarget.appendChild(template)
  }

  removeVariable(event) {
    const variableItem = event.target.closest('.variable-item')
    if (variableItem) {
      variableItem.remove()
    }
  }

  toggleIndefinite(event) {
    const isChecked = event.target.checked
    if (this.hasExperimentEndInputTarget) {
      this.experimentEndInputTarget.disabled = isChecked
      if (isChecked) {
        this.experimentEndInputTarget.value = ''
      }
    }
  }

  async submitForm(event) {
    event.preventDefault()

    if (!this.validateCurrentStep()) return

    this.submitBtnTarget.disabled = true
    this.submitBtnTarget.textContent = 'Creating...'

    const payload = this.buildPayload()

    try {
      const response = await fetch('/api/v1/experiments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Failed to create experiment')
      }

      const data = await response.json()
      this.showNotification('Experiment created successfully!', 'success')

      // Redirect to the new experiment
      window.location.href = `/dashboard/experiments/${data.experiment.id}`
    } catch (error) {
      console.error('Error creating experiment:', error)
      this.showNotification(error.message || 'Failed to create experiment', 'error')
      this.submitBtnTarget.disabled = false
      this.submitBtnTarget.textContent = 'Create Experiment'
    }
  }

  buildPayload() {
    // Gather variables
    const variables = []
    const variableItems = this.variablesListTarget.querySelectorAll('.variable-item')
    variableItems.forEach((item) => {
      variables.push({
        name: item.querySelector('input[name*="[name]"]')?.value || '',
        variable_type: item.querySelector('select[name*="[variable_type]"]')?.value || 'supplement',
        dosage: item.querySelector('input[name*="[dosage]"]')?.value || '',
        frequency: item.querySelector('input[name*="[frequency]"]')?.value || ''
      })
    })

    // Gather metrics
    const metrics = this.metricCheckboxTargets
      .filter((cb) => cb.checked)
      .map((cb) => ({ metric_type: cb.value }))

    return {
      experiment: {
        name: this.nameInputTarget.value,
        hypothesis: this.hypothesisInputTarget?.value || '',
        description: this.descriptionInputTarget?.value || '',
        visibility: this.visibilityInputTarget?.value || 'private',
        baseline_start_date: this.baselineStartInputTarget?.value || null,
        baseline_end_date: this.baselineEndInputTarget?.value || null,
        experiment_start_date: this.experimentStartInputTarget?.value || null,
        experiment_end_date: this.indefiniteCheckboxTarget?.checked ? null : (this.experimentEndInputTarget?.value || null),
        variables_attributes: variables,
        metrics_attributes: metrics
      }
    }
  }

  showNotification(message, type = 'success') {
    const notification = document.createElement('div')
    notification.className = `fixed top-4 right-4 z-50 p-4 border-2 ${
      type === 'success' ?
        'bg-green-50 border-green-500 text-green-700' :
        'bg-red-50 border-red-500 text-red-700'
    }`
    notification.textContent = message

    document.body.appendChild(notification)

    setTimeout(() => {
      notification.remove()
    }, 3000)
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }
}
